/* eslint-disable @typescript-eslint/no-explicit-any */
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import * as React from "react";
import { showSnackbar } from "../../../service/snackbar.service";

export const GeneralPopoverWrapperCloseTrigger =
  "GeneralPopoverWrapperCloseTrigger";

type HorizontalOrigin = "center" | "right" | "left";

const DEFAULT_FLIP_MIN_SPACE = 420;

function resolveHorizontalOrigins(
  preferredAnchor: HorizontalOrigin,
  preferredTransform: HorizontalOrigin,
  triggerRect: DOMRect,
  minSpace: number
): { anchor: HorizontalOrigin; transform: HorizontalOrigin } {
  if (preferredAnchor === "center") {
    return { anchor: preferredAnchor, transform: preferredTransform };
  }

  const spaceRight = window.innerWidth - triggerRect.right;
  const spaceLeft = triggerRect.left;

  // Preferred open-to-the-right: anchor right + transform left
  const opensRight =
    preferredAnchor === "right" && preferredTransform === "left";
  // Preferred open-to-the-left: anchor left + transform right
  const opensLeft =
    preferredAnchor === "left" && preferredTransform === "right";

  if (opensRight && spaceRight < minSpace && spaceLeft > spaceRight) {
    return { anchor: "left", transform: "right" };
  }
  if (opensLeft && spaceLeft < minSpace && spaceRight > spaceLeft) {
    return { anchor: "right", transform: "left" };
  }

  // Also handle "same-edge" alignment that grows opposite:
  // anchor right + transform right → grows leftward
  if (
    preferredAnchor === "right" &&
    preferredTransform === "right" &&
    spaceLeft < minSpace &&
    spaceRight > spaceLeft
  ) {
    return { anchor: "left", transform: "left" };
  }
  if (
    preferredAnchor === "left" &&
    preferredTransform === "left" &&
    spaceRight < minSpace &&
    spaceLeft > spaceRight
  ) {
    return { anchor: "right", transform: "right" };
  }

  return { anchor: preferredAnchor, transform: preferredTransform };
}

export default function GeneralPopoverWrapper({
  transformVertical,
  transformHorizontal,
  anchorVertical,
  anchorHorizontal,
  triggerElement,
  popoverContent,
  tooltipTitle,
  closeOnClickContent,
  disableCloseOnClickOutside,
  onClickTriggerElement,
  disablePop,
  disabledReason,
  onClose,
  transitionDuration,
  stopPropagationOnClick,
  fullWidth,
  autoFlipHorizontal,
  flipMinSpacePx,
  disablePortal,
}: {
  transformVertical?: "top" | "bottom" | "center";
  transformHorizontal?: HorizontalOrigin;
  anchorVertical?: "top" | "bottom" | "center";
  anchorHorizontal?: HorizontalOrigin;
  triggerElement?: JSX.Element;
  popoverContent?: JSX.Element;
  tooltipTitle?: string;
  closeOnClickContent?: boolean;
  disableCloseOnClickOutside?: boolean;
  onClickTriggerElement?: () => void;
  disablePop?: boolean;
  disabledReason?: string;
  onClose?: () => void;
  transitionDuration?: number;
  stopPropagationOnClick?: boolean;
  fullWidth?: boolean;
  /** When true, flip horizontal open side if the preferred side lacks space. */
  autoFlipHorizontal?: boolean;
  /** Minimum free space (px) required on the preferred side before flipping. */
  flipMinSpacePx?: number;
  disablePortal?: boolean;
}) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const preferredAnchor = anchorHorizontal || "left";
  const preferredTransform = transformHorizontal || "left";
  const [resolvedAnchorHorizontal, setResolvedAnchorHorizontal] =
    React.useState<HorizontalOrigin>(preferredAnchor);
  const [resolvedTransformHorizontal, setResolvedTransformHorizontal] =
    React.useState<HorizontalOrigin>(preferredTransform);

  const handleClick = (event: any) => {
    if (disablePop) {
      if (disabledReason) {
        showSnackbar("danger", "Error", disabledReason);
      }
      return;
    }
    if (stopPropagationOnClick) event.stopPropagation();
    onClickTriggerElement && onClickTriggerElement();

    const target = event.currentTarget as HTMLElement;
    if (autoFlipHorizontal) {
      const resolved = resolveHorizontalOrigins(
        preferredAnchor,
        preferredTransform,
        target.getBoundingClientRect(),
        flipMinSpacePx ?? DEFAULT_FLIP_MIN_SPACE
      );
      setResolvedAnchorHorizontal(resolved.anchor);
      setResolvedTransformHorizontal(resolved.transform);
    } else {
      setResolvedAnchorHorizontal(preferredAnchor);
      setResolvedTransformHorizontal(preferredTransform);
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose && onClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Tooltip title={tooltipTitle} arrow placement="top">
        <Box
          onClick={handleClick}
          aria-describedby={id}
          sx={{
            width: fullWidth ? "100%" : undefined,
            flex: fullWidth ? 1 : undefined,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: fullWidth ? undefined : "center",
          }}
        >
          {triggerElement}
        </Box>
      </Tooltip>

      <Popover
        transitionDuration={
          transitionDuration != undefined ? transitionDuration : undefined
        }
        id={id}
        open={open}
        anchorEl={anchorEl}
        disablePortal={disablePortal}
        onClose={!disableCloseOnClickOutside ? handleClose : undefined}
        anchorOrigin={{
          vertical: anchorVertical || "bottom",
          horizontal: resolvedAnchorHorizontal,
        }}
        transformOrigin={{
          vertical: transformVertical || "top",
          horizontal: resolvedTransformHorizontal,
        }}
      >
        <Box
          onClick={
            closeOnClickContent
              ? handleClose
              : (e) => {
                  const className: string = (e.target as any).className;
                  className?.includes &&
                    className?.includes(GeneralPopoverWrapperCloseTrigger) &&
                    handleClose();
                }
          }
        >
          {popoverContent}
        </Box>
      </Popover>
    </Box>
  );
}
